import { createFileRoute, useNavigate, useParams, useRouteContext } from '@tanstack/react-router';
import { ScrollText } from 'lucide-react';
import { RecipeForm } from '@/features/recipes/RecipeForm';
import { useRecipe } from '@/features/recipes/api';
import { useUpdateRecipe } from '@/features/recipes/mutations';
import { recipeToForm, defaultFormValues } from '@/features/recipes/formMapping';
import { Button } from '@/components/ui/Button';
import { RecipeLogDialog } from '@/features/ai-logs/RecipeLogDialog';
import { useMemo, useState } from 'react';

export const Route = createFileRoute('/_authed/recipes/$id/edit')({
  component: EditRecipe,
});

function EditRecipe() {
  const { id } = useParams({ from: '/_authed/recipes/$id/edit' });
  const navigate = useNavigate();
  const { me } = useRouteContext({ from: '/_authed' });
  const { data, isLoading, error } = useRecipe(id);
  const update = useUpdateRecipe(id);
  const [logOpen, setLogOpen] = useState(false);

  const initial = useMemo(() => (data ? recipeToForm(data) : defaultFormValues()), [data]);

  if (isLoading) {
    return <div className="p-4 sm:p-6 xl:p-8 text-ink-soft">Loading…</div>;
  }
  if (error || !data) {
    return (
      <div className="p-4 sm:p-6 xl:p-8">
        <h1 className="font-serif text-2xl mb-2">Recipe not found</h1>
        <p className="text-ink-soft">{error instanceof Error ? error.message : 'Unable to load.'}</p>
      </div>
    );
  }

  return (
    <div>
      <header className="px-4 pt-4 sm:px-6 sm:pt-6 xl:px-8 xl:pt-8 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h1 className="font-serif text-3xl leading-tight">Edit recipe</h1>
          <p className="text-xs text-ink-faint font-mono mt-1">{id}</p>
        </div>
        <Button variant="secondary" size="sm" onClick={() => setLogOpen(true)}>
          <ScrollText size={14} /> View generation log
        </Button>
      </header>
      <RecipeForm
        mode="edit"
        me={me}
        ownerId={data.ownerId}
        recipeId={id}
        recipeImagePrompt={data.imagePrompt}
        initialValues={initial}
        onSave={async (values) => {
          await update.mutateAsync(values);
          await navigate({ to: '/recipes' });
        }}
        onCancel={async () => { await navigate({ to: '/recipes' }); }}
      />
      {logOpen && <RecipeLogDialog recipeId={id} onClose={() => setLogOpen(false)} />}
    </div>
  );
}
